import type { EarnResponse } from '@xchange/shared';
import { useCallback, useEffect, useState } from 'react';
import { api, type PairView } from '../api.js';
import { assetName, type Asset } from '../assets.js';
import {
  QUEST_TEXT,
  bidPreset,
  jobLine,
  ordinal,
  pairToBid,
  questStatus,
  quizXgeLine,
  usdText,
  wholeXge,
} from '../earn.js';
import { toast } from '../toast.js';
import { track } from '../track.js';
import { Mark, perCoin } from './Market.js';

/** A bid to fill into the ticket: the pair, its price and amount in the pair's own units. */
export interface BidPreset {
  readonly pair: string;
  readonly price: bigint;
  readonly amount: bigint;
}

/**
 * Earn: the daily job, made visible. Today's pool and your share, your bid
 * against the band, the ladder of first steps, and today's board. Read once
 * when it opens, and again on Refresh; never on a timer.
 *
 * The same screen in a browser and inside Telegram. XGE is paid for bids on
 * the book and for swaps; nothing here is paid for opening it.
 */
export function Earn({
  pairs,
  wordsDone,
  funded,
  onBid,
}: {
  pairs: PairView[];
  /** The words were typed back on this device: the app knows it before the server does. */
  wordsDone: boolean;
  /** The wallet holds something now, read from the chain by the screen around this one. */
  funded: boolean;
  /** Open the ticket with this bid filled in. */
  onBid: (preset: BidPreset | null, pair: string | null) => void;
}) {
  const [earn, setEarn] = useState<EarnResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback(async () => {
    try {
      setEarn(await api.earn());
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);
  useEffect(() => {
    track('earn_open');
    void load();
  }, [load]);

  if (earn === null)
    return (
      <div className="wt-card" data-testid="earn">
        <div className="wt-empty">{failed ? 'Could not load. Try again.' : 'Loading…'}</div>
        {failed && (
          <button className="wt-secondary" onClick={() => void load()}>
            Try again
          </button>
        )}
      </div>
    );

  const pairOf = (name: string): PairView | undefined => pairs.find((p) => p.name === name);
  const preset = (row: EarnResponse['job']['pairs'][number]): BidPreset | null => {
    const p = pairOf(row.pair);
    return p === undefined ? null : bidPreset(p, row, earn.job.minUsd);
  };
  const job = earn.job;
  const withBid = job.pairs.filter((r) => r.bid !== null);
  const target = pairToBid(earn);
  const quests = earn.quests.map((q) =>
    !q.done && ((q.id === 'words' && wordsDone) || (q.id === 'funds' && funded))
      ? { ...q, done: true }
      : q,
  );
  const next = quests.findIndex((q) => !q.done);

  return (
    <div className="earn" data-testid="earn">
      {earn.quiz.on && (
        <QuizCard
          quiz={earn.quiz}
          onAnswered={(q) => {
            setEarn({ ...earn, quiz: q });
          }}
        />
      )}
      <section className="wt-card" data-testid="earn-today">
        <div className="wt-card-title">
          Today
          <span className="wt-card-sub">
            {earn.day} UTC ·{' '}
            <button className="wt-link" onClick={() => void load()} data-testid="earn-refresh">
              Refresh
            </button>
          </span>
        </div>
        <div className="earn-figures">
          <Figure id="earn-left" label="Left to share today" xge={earn.today.leftXge} />
          <Figure id="earn-share" label="Your share so far" xge={earn.today.yourShareXge} />
          <Figure
            id="earn-paid"
            label="Paid to you since midnight"
            xge={earn.today.paidSinceMidnightXge}
          />
        </div>
        <p className="wt-note">
          XGE is paid after midnight UTC for bids on the book and for swaps.
        </p>
        {earn.rules.feeBound && (
          <p className="wt-note" data-testid="earn-fee-bound">
            XGE paid to you is at most the fees you paid this week.
          </p>
        )}
      </section>

      <section className="wt-card" data-testid="earn-job">
        <div className="wt-card-title">
          Your daily job
          {job.streak > 0 && (
            <span className="wt-card-sub" data-testid="earn-streak">
              Day {job.streak} on the book
            </span>
          )}
        </div>
        {withBid.length === 0 ? (
          <>
            <p className="earn-line" data-testid="earn-no-bid">
              Place a bid of {usdText(job.minUsd).replace(/\.00$/, '')} or more within{' '}
              {job.bandPercent}% of the best offer.
            </p>
            <button
              className="wt-submit buy"
              onClick={() => {
                track('earn_place');
                onBid(target === null ? null : preset(target), target?.pair ?? null);
              }}
              data-testid="earn-place"
            >
              Place a bid
            </button>
          </>
        ) : (
          withBid.map((row) => {
            const p = pairOf(row.pair);
            const base = (p?.base ?? 'XGE') as Asset;
            return (
              <div className="earn-job" key={row.pair} data-testid={`earn-job-${row.pair}`}>
                <div className="earn-job-head">
                  <Mark asset={base} size={18} />
                  <span>
                    Your {assetName[base]} bid of {row.bid === null ? '' : usdText(row.bid.usdc)}
                    {p !== undefined && row.bid !== null && (
                      <span className="earn-job-at"> at {perCoin(p, row.bid.price)}</span>
                    )}
                  </span>
                  <span className={`earn-badge${row.inside ? ' in' : ''}`}>
                    {row.inside ? 'Inside' : 'Outside'}
                  </span>
                </div>
                <p className="wt-note" data-testid={`earn-distance-${row.pair}`}>
                  {jobLine(row, job.bandPercent, job.minUsd)}
                </p>
                {!row.inside && row.from !== null && (
                  <button
                    className="wt-submit buy"
                    onClick={() => {
                      track('earn_move');
                      onBid(preset(row), row.pair);
                    }}
                    data-testid={`earn-move-${row.pair}`}
                  >
                    Move my bid to {job.bandPercent}%
                  </button>
                )}
              </div>
            );
          })
        )}
        {!job.counts && (
          <p className="wt-note" data-testid="earn-needs-trade">
            Your bids count once you have swapped $10 with someone new.
          </p>
        )}
        {job.hoursToday > 0 && (
          <p className="wt-note" data-testid="earn-hours">
            Counted {job.hoursToday} {job.hoursToday === 1 ? 'hour' : 'hours'} today.
          </p>
        )}
        {!job.liquidityOn && <p className="wt-note">Bids are not rewarded today.</p>}
      </section>

      <section className="wt-card" data-testid="earn-ladder">
        <div className="wt-card-title">
          The ladder
          <span className="wt-card-sub">
            {quests.filter((q) => q.done).length} of {quests.length}
          </span>
        </div>
        <div className="wt-steps">
          {quests.map((q, i) => {
            const status = questStatus(q, earn.ladder.minCountedDays);
            return (
              <div
                className={`wt-step${q.done ? ' done' : i === next ? ' next' : ''}`}
                key={q.id}
                data-testid={`earn-quest-${q.id}`}
                data-done={q.done ? 'yes' : 'no'}
              >
                <span className="mark">{q.done ? '✓' : i + 1}</span>
                <span className="what">
                  {QUEST_TEXT[q.id]}
                  {status !== '' && <span className="why">{status}</span>}
                </span>
                {q.xge !== '0' && <span className="earn-xge">{wholeXge(q.xge)} XGE</span>}
              </div>
            );
          })}
        </div>
      </section>

      <section className="wt-card" data-testid="earn-board">
        <div className="wt-card-title">
          Today's board
          <span className="wt-card-sub">hours a bid counted</span>
        </div>
        {earn.board.rows.length === 0 ? (
          <div className="wt-empty">No bid has counted yet today.</div>
        ) : (
          earn.board.rows.map((r) => (
            <div
              className={`earn-board-row${r.you ? ' you' : ''}`}
              key={r.rank}
              data-testid="earn-board-row"
            >
              <span className="earn-rank">{r.rank}</span>
              <span className="earn-name">
                {r.name}
                {r.you ? ' (you)' : ''}
              </span>
              <span className="earn-hours">{r.hours} h</span>
            </div>
          ))
        )}
        {earn.board.you !== null && earn.board.you.rank > earn.board.rows.length && (
          <p className="wt-note" data-testid="earn-board-you">
            You are {ordinal(earn.board.you.rank)} with {earn.board.you.hours} h.
          </p>
        )}
      </section>

      {/* Only under `pnpm try`: an hour of the book counted now, a past day
          counted, and the two farming rules switched, so every state can be
          pressed through. */}
      {import.meta.env.DEV && (
        <section className="wt-card earn-dev" data-testid="earn-dev">
          <div className="wt-card-title">Test only</div>
          <div className="earn-dev-row">
            {[1, 24].map((hours) => (
              <button
                key={hours}
                className="wt-secondary"
                onClick={() => {
                  void api
                    .dev('coordinate', { hours })
                    .then(load)
                    .catch(() => {
                      toast.err('Works only with pnpm try.');
                    });
                }}
                data-testid={`earn-dev-hours-${hours}`}
              >
                Count {hours === 1 ? 'an hour' : '24 hours'}
              </button>
            ))}
            <button
              className="wt-secondary"
              onClick={() => {
                // One more day back than those counted so far, as if the bid had stood then.
                void api
                  .dev('coordinate', { daysAgo: Math.min(7, Math.max(1, earn.ladder.countedDays)) })
                  .then(load)
                  .catch(() => {
                    toast.err('Works only with pnpm try.');
                  });
              }}
              data-testid="earn-dev-past-day"
            >
              Count a past day
            </button>
            <button
              className="wt-secondary"
              onClick={() => {
                const on = !earn.rules.bidNeedsFirstTrade;
                void api
                  .dev('rules', {
                    bidNeedsFirstTrade: on,
                    pairOnceADay: on,
                    tradesUnlinkedOnly: on,
                    referenceStrict: on,
                    bidFullHour: on,
                    feeBound: on,
                  })
                  .then(load)
                  .catch(() => {
                    toast.err('Works only with pnpm try.');
                  });
              }}
              data-testid="earn-dev-rules"
            >
              Farming rules {earn.rules.bidNeedsFirstTrade ? 'on' : 'off'}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * The day's three questions: one at a time, four answers as buttons, one tap
 * locks it, the right one and a line of why are shown, then the next. The
 * server holds the answer key and checks each answer once.
 */
function QuizCard({
  quiz,
  onAnswered,
}: {
  quiz: EarnResponse['quiz'];
  onAnswered: (q: EarnResponse['quiz']) => void;
}) {
  const firstOpen = quiz.questions.findIndex((q) => q.picked === null);
  const [at, setAt] = useState(firstOpen < 0 ? quiz.questions.length : firstOpen);
  const [busy, setBusy] = useState(false);
  const total = quiz.questions.length;
  const done = at >= total;
  const q = quiz.questions[at];
  const answered = q !== undefined && q.picked !== null;

  const pick = (choice: number): void => {
    if (q === undefined || answered || busy) return;
    setBusy(true);
    track('quiz_answer', { id: q.id });
    api
      .quizAnswer(q.id, choice)
      .then(onAnswered)
      .catch(() => {
        toast.err('Could not send your answer. Try again.');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <section className="wt-card quiz" data-testid="earn-quiz">
      <div className="wt-card-title">
        Three questions
        <span className="wt-card-sub" data-testid="quiz-progress">
          {done ? 'Done for today' : `${at + 1} of ${total}`}
        </span>
      </div>
      {done || q === undefined ? (
        <>
          <p className="earn-line" data-testid="quiz-done">
            {quiz.rightToday} of {total} right today. Three new questions after midnight UTC.
          </p>
        </>
      ) : (
        <div className="quiz-q" data-testid={`quiz-q-${q.id}`}>
          <span className="quiz-cat">{q.category}</span>
          <p className="quiz-text">{q.text}</p>
          <div className="quiz-choices">
            {q.choices.map((c, i) => {
              const state = !answered
                ? ''
                : i === q.answer
                  ? ' right'
                  : i === q.picked
                    ? ' wrong'
                    : ' dim';
              return (
                <button
                  key={i}
                  className={`quiz-choice${state}`}
                  disabled={answered || busy}
                  onClick={() => {
                    pick(i);
                  }}
                  data-testid={`quiz-choice-${i}`}
                >
                  <span className="quiz-choice-mark">
                    {answered && i === q.answer ? '✓' : answered && i === q.picked ? '✕' : ''}
                  </span>
                  {c}
                </button>
              );
            })}
          </div>
          {answered && (
            <div className="quiz-after" data-testid="quiz-after">
              <p className="quiz-verdict">{q.picked === q.answer ? 'Right.' : 'Not quite.'}</p>
              <p className="wt-note">{q.why}</p>
              <button
                className="wt-submit buy"
                onClick={() => {
                  setAt(at + 1);
                }}
                data-testid="quiz-next"
              >
                {at + 1 >= total ? 'Done' : 'Next'}
              </button>
            </div>
          )}
        </div>
      )}
      <p className="wt-note" data-testid="quiz-xge">
        {quizXgeLine(quiz)}
      </p>
      {!quiz.budgetLeft && (
        <p className="wt-note">Today's share is used up; paid on a later day.</p>
      )}
    </section>
  );
}

function Figure({ id, label, xge }: { id: string; label: string; xge: string }) {
  return (
    <div className="earn-figure" data-testid={id}>
      {/* Whole XGE: three figures side by side on a phone have no room for decimals. */}
      <span className="earn-figure-value">{wholeXge((BigInt(xge) / 1_000_000n) * 1_000_000n)}</span>
      <span className="earn-figure-label">{label}</span>
    </div>
  );
}
